import React, { useEffect, useState } from 'react';
import { useGame } from '@/contexts/GameContext';
import { useShop } from '@/contexts/ShopContext';
import { Modal } from '@/components/ui';

interface StatsOverlayProps {
  isOpen: boolean;
  onClose: () => void;
}

const StatsOverlay: React.FC<StatsOverlayProps> = ({ isOpen, onClose }) => {
  const { gameState } = useGame();
  const { shopState } = useShop();
  const [totalPlayTime, setTotalPlayTime] = useState(0);

  // Calculate total play time
  useEffect(() => {
    const startTime = Date.now();
    const interval = setInterval(() => {
      setTotalPlayTime(Math.floor((Date.now() - startTime) / 1000));
    }, 1000);

    return () => clearInterval(interval);
  }, []);

  // Close on Escape or Tab
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if ((e.key === 'Escape' || e.key === 'Tab') && isOpen) {
        e.preventDefault();
        onClose();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  // Calculate statistics
  const totalLevelsCompleted = gameState.completedLevels.length;
  const perfectLevels = Object.values(gameState.levelStats).filter(stat => stat.stars === 3).length;
  const totalAttempts = Object.values(gameState.levelStats).reduce((sum, stat) => sum + stat.totalAttempts, 0);
  const totalScore = gameState.totalScore;
  const currentStreak = gameState.gameStats.streak;
  const achievements = gameState.achievements.length;

  const formatTime = (seconds: number): string => {
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    const secs = seconds % 60;
    
    if (hours > 0) {
      return `${hours}h ${minutes}m`;
    }
    return `${minutes}m ${secs}s`;
  };

  const modalContent = (
    <>
      <div className="stats-overlay-header">
        <h2 className="stats-title">
          Game Statistics
        </h2>
      </div>

      <div className="stats-grid">
          {/* Progress Stats */}
          <div className="stats-section">
            <h3 className="stats-section-title">Progress</h3>
            <div className="stat-row">
              <span className="stat-label">Levels Completed:</span>
              <span className="stat-value">{totalLevelsCompleted}/{gameState.totalLevels || 50}</span>
            </div>
            <div className="stat-row">
              <span className="stat-label">Perfect Levels:</span>
              <span className="stat-value">{perfectLevels} (3-star)</span>
            </div>
            <div className="stat-row">
              <span className="stat-label">Total Attempts:</span>
              <span className="stat-value">{totalAttempts}</span>
            </div>
            <div className="stat-row">
              <span className="stat-label">Current Streak:</span>
              <span className="stat-value">{currentStreak}</span>
            </div>
          </div>

          {/* Score Stats */}
          <div className="stats-section">
            <h3 className="stats-section-title">Scoring</h3>
            <div className="stat-row">
              <span className="stat-label">Total Score:</span>
              <span className="stat-value">{totalScore.toLocaleString()}</span>
            </div>
            <div className="stat-row">
              <span className="stat-label">Coins Collected:</span>
              <span className="stat-value">{shopState.coins}</span>
            </div>
            <div className="stat-row">
              <span className="stat-label">Gems Earned:</span>
              <span className="stat-value">{shopState.gems}</span>
            </div>
            <div className="stat-row">
              <span className="stat-label">Lives Remaining:</span>
              <span className="stat-value">{shopState.lives}</span>
            </div>
          </div>

          {/* Time Stats */}
          <div className="stats-section">
            <h3 className="stats-section-title">Time</h3>
            <div className="stat-row">
              <span className="stat-label">Session Time:</span>
              <span className="stat-value">{formatTime(totalPlayTime)}</span>
            </div>
            <div className="stat-row">
              <span className="stat-label">Best Level Time:</span>
              <span className="stat-value">
                {Math.min(...Object.values(gameState.levelStats).map(s => s.bestTime).filter(t => t < Infinity)) || 0}s
              </span>
            </div>
            <div className="stat-row">
              <span className="stat-label">Average Time:</span>
              <span className="stat-value">
                {Math.round(
                  Object.values(gameState.levelStats).reduce((sum, s) => sum + s.bestTime, 0) /
                  Math.max(1, Object.values(gameState.levelStats).length)
                )}s
              </span>
            </div>
          </div>

          {/* Achievement Stats */}
          <div className="stats-section">
            <h3 className="stats-section-title">Achievements</h3>
            <div className="stat-row">
              <span className="stat-label">Unlocked:</span>
              <span className="stat-value">{achievements}/20</span>
            </div>
            <div className="stat-row">
              <span className="stat-label">Latest:</span>
              <span className="stat-value">
                {gameState.achievements[gameState.achievements.length - 1] || 'None yet'}
              </span>
            </div>
          </div>
        </div>

      <div className="stats-footer">
        <p className="stats-hint">Press Tab or Escape to close</p>
      </div>
    </>
  );

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      variant="default"
      size="large"
      showCloseButton={true}
      closeOnBackdrop={true}
      closeOnEscape={true}
      className="stats-overlay-wrapper"
    >
      {modalContent}
    </Modal>
  );
};

export default StatsOverlay;